// src/views/Clientes/DescuentoFlota/EditarDescuentoFlota.jsx
import React, { useEffect, useMemo, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

export default function EditarDescuentoFlota({ id, onClose, onVolver, onSaved }) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || (() => {});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [original, setOriginal] = useState(null);

  // READ ONLY
  const [cliente, setCliente] = useState("");
  const [eess, setEess] = useState("");
  const [producto, setProducto] = useState("");
  const [tarjeta, setTarjeta] = useState("");

  // VALORES (PYG)
  const [valorTotal, setValorTotal] = useState("");
  const [valorEess, setValorEess] = useState("");
  const [valorPetr, setValorPetr] = useState("");

  // PORCENTAJES
  const [porcTotal, setPorcTotal] = useState("");
  const [porcEess, setPorcEess] = useState("");
  const [porcPetr, setPorcPetr] = useState("");

  // FECHAS
  const [fechaIni, setFechaIni] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // =========================
  // CARGA INICIAL
  // =========================
  useEffect(() => {
    if (!id) return;

    (async () => {
      setLoading(true);
      try {
        const dto = await apiFetchJson(apiRoutes.descuentoFlota.detail(id));
        setOriginal(dto);

        setCliente(dto.clienteNombre ?? "");
        setEess(dto.eessNombre ?? "TODAS");
        setProducto(dto.productoNombre ?? "TODOS");
        setTarjeta(dto.tarjetaNumero ?? "TODAS");

        setValorTotal(dto.valorDescuento ?? "");
        setValorEess(dto.valorEess ?? "");
        setValorPetr(dto.valorPetr ?? "");

        setPorcTotal(dto.porcentajeDescuento ?? "");
        setPorcEess(dto.porcentajeEess ?? "");
        setPorcPetr(dto.porcentajePetr ?? "");

        setFechaIni(dto.fechaInicio ?? "");
        setFechaFin(dto.fechaFin ?? "");
      } catch (e) {
        setError(e?.message || "Error al cargar el descuento");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // =========================
  // CÁLCULOS AUTOMÁTICOS
  // =========================

  // --- VALORES → Petrobras ---
  useEffect(() => {
    const t = Number(valorTotal || 0);
    const e = Number(valorEess || 0);

    // 🔹 si Total Valor se borra o queda en 0 → limpiar relacionados
    if (valorTotal === "" || t === 0) {
      setValorEess("");
      setValorPetr("");
      return;
    }

    if (isNaN(t) || isNaN(e)) {
      setValorPetr("");
      return;
    }

    setValorPetr(Math.max(0, t - e));
  }, [valorTotal, valorEess]);

  // --- PORCENTAJES → Petrobras ---
  useEffect(() => {
    const t = Number(porcTotal || 0);
    const e = Number(porcEess || 0);

    // 🔹 si Total % se borra o queda en 0 → limpiar relacionados
    if (porcTotal === "" || t === 0) {
      setPorcEess("");
      setPorcPetr("");
      return;
    }

    if (isNaN(t) || isNaN(e)) {
      setPorcPetr("");
      return;
    }

    setPorcPetr(Math.max(0, 100 - e));
  }, [porcTotal, porcEess]);

  // =========================
  // VALIDACIÓN EXCLUSIVA
  // =========================
  const conflictoTipo = useMemo(() => {
    const tieneValor =
      Number(valorTotal || 0) > 0 || Number(valorEess || 0) > 0;
    const tienePorc =
      Number(porcTotal || 0) > 0 || Number(porcEess || 0) > 0;
    return tieneValor && tienePorc;
  }, [valorTotal, valorEess, porcTotal, porcEess]);

  // =========================
  // CHANGED (EXACTO COMO PEDISTE)
  // =========================
  const changed = useMemo(() => {
    if (!original) return false;

    const n = (v) =>
      v === "" || v === null || v === undefined ? 0 : Number(v);

    return (
      n(valorTotal) !== n(original.valorDescuento) ||
      n(valorEess) !== n(original.valorEess) ||
      n(porcTotal) !== n(original.porcentajeDescuento) ||
      n(porcEess) !== n(original.porcentajeEess) ||
      fechaIni !== (original.fechaInicio ?? "") ||
      fechaFin !== (original.fechaFin ?? "")
    );
  }, [
    original,
    valorTotal,
    valorEess,
    porcTotal,
    porcEess,
    fechaIni,
    fechaFin,
  ]);

  // =========================
  // HELPERS
  // =========================
  const toNumOrZero = (v) =>
    v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v);

  // =========================
  // GUARDAR
  // =========================
  const handleSave = async () => {
    if (!changed || conflictoTipo) return;

    setSaving(true);
    setError("");

    const usandoValores =
      Number(valorTotal || 0) > 0 || Number(valorEess || 0) > 0;

    const usandoPorcentajes =
      Number(porcTotal || 0) > 0 || Number(porcEess || 0) > 0;

    try {
      await appFetchJson(apiRoutes.descuentoFlota.update(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          valorDescuento: usandoValores ? toNumOrZero(valorTotal) : 0,
          valorEess: usandoValores ? toNumOrZero(valorEess) : 0,
          valorPetr: usandoValores ? toNumOrZero(valorPetr) : 0,

          prDescuento: usandoPorcentajes ? toNumOrZero(porcTotal) : 0,
          prEess: usandoPorcentajes ? toNumOrZero(porcEess) : 0,
          prPetr: usandoPorcentajes ? toNumOrZero(porcPetr) : 0,

          fechaInicio: fechaIni || null,
          fechaFin: fechaFin || null,
        }),
      });

      savedFn();
      closeFn();
    } catch (e) {
      setError(e?.message || "Error al guardar el descuento");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title">Editar Descuento Flota</h2>
        </div>

        <div className="modal-body">
          {conflictoTipo && (
            <div className="mb-3 text-red-600 font-medium">
              Solo debe cargar valores en montos o valores en porcentajes, no ambos.
            </div>
          )}

          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            <FilaRead label="Cliente" value={cliente} />
            <FilaRead label="EE.SS." value={eess} />
            <FilaRead label="Producto" value={producto} />
            <FilaRead label="Tarjeta" value={tarjeta} />

            <FilaEdit
              label="Total Valor PYG"
              input={
                <input
                  className="input"
                  type="number"
                  value={valorTotal}
                  onChange={(e) => setValorTotal(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Valor EE.SS. PYG"
              input={
                <input
                  className="input"
                  type="number"
                  value={valorEess}
                  onChange={(e) => setValorEess(e.target.value)}
                />
              }
            />

            <FilaRead label="Valor Petrobras PYG" value={valorPetr || "—"} />

            <FilaEdit
              label="Total Descuento %"
              input={
                <input
                  className="input"
                  type="number"
                  value={porcTotal}
                  onChange={(e) =>
                    setPorcTotal(e.target.value.replace(",", "."))
                  }
                />
              }
            />

            <FilaEdit
              label="EE.SS. %"
              input={
                <input
                  className="input"
                  type="number"
                  value={porcEess}
                  onChange={(e) =>
                    setPorcEess(e.target.value.replace(",", "."))
                  }
                />
              }
            />

            <FilaRead label="Petrobras %" value={porcPetr || "—"} />

            <FilaEdit
              label="Fecha Inicio"
              input={
                <input
                  className="input"
                  type="date"
                  value={fechaIni}
                  onChange={(e) => setFechaIni(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Fecha Fin"
              input={
                <input
                  className="input"
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              }
            />
          </div>
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              isValid={changed && !conflictoTipo}
              saving={saving}
              onClick={handleSave}
            />
            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */

function FilaRead({ label, value }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1 py-2 font-medium">{value ?? "—"}</div>
    </div>
  );
}

function FilaEdit({ label, input }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{input}</div>
    </div>
  );
}
